import { Box, FormControl, RadioGroup } from '@mui/material';
import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import StyledRadio from './StyledRadio';
import Flex from '../Flex/Flex';
import { Text } from '../Generic/Text';
import colors from 'src/utils/colors';

export interface RadioOption<T extends string> {
  label: string;
  description: string;
  comingSoon?: boolean;
  width?: string;
}

interface ControlledRadioGroupProps<T extends FieldValues, K extends Path<T>> {
  control: Control<T>;
  name: K;
  options: Record<string, RadioOption<string>>;
  label?: string;
  description?: string;
  onChange?: (newValue: string) => void;
  gap?: number;
  width?: string;
}

export const ControlledRadioGroup = <T extends FieldValues, K extends Path<T>>({
  control,
  name,
  options,
  label,
  description,
  onChange,
  gap = 104,
  width = '100%'
}: ControlledRadioGroupProps<T, K>) => {
  return (
    <Flex flexDirection="column" gap={32}>
      {(label || description) && (
        <Flex flexDirection="column" gap={8}>
          {label && (
            <Text variant="h4" lineHeight="28px">
              {label}
            </Text>
          )}
          {description && (
            <Box fontSize={16} lineHeight="24px">
              {description}
            </Box>
          )}
        </Flex>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormControl error={!!error}>
              <Flex gap={8} flexDirection="column">
                <RadioGroup
                  value={field.value?.id || ''}
                  onChange={(_, newValue) => {
                    const selectedOption = options[newValue];
                    field.onChange({ id: newValue, name: selectedOption?.label });
                    onChange?.(newValue);
                  }}
                >
                  <Flex gap={gap} width={width}>
                    {Object.entries(options).map(([type, { label, description, comingSoon, width }], index) => (
                      <Box
                        component="label"
                        htmlFor={type+index}
                        key={type+index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          opacity: comingSoon ? 0.5 : 1,
                          cursor: comingSoon ? 'default' : 'pointer',
                          width: width || '300px'
                        }}
                      >
                        <StyledRadio
                          id={type + index}
                          value={type}
                          disabled={comingSoon}
                          sx={{
                            marginTop: '4px',
                            '&.Mui-disabled': {
                              color: colors.grey
                            }
                          }}
                        />
                        <Flex gap={8} flexDirection="column" sx={{ pointerEvents: 'none' }}>
                          <Text
                            variant="h6"
                            lineHeight="24px"
                            sx={{
                              color: comingSoon ? colors.grey : colors.darkBlue
                            }}
                          >
                            {label}
                          </Text>
                          <Text
                            variant="body2"
                            lineHeight="20px"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              color: comingSoon ? colors.grey : colors.darkBlue
                            }}
                          >
                            {description}
                          </Text>
                        </Flex>
                      </Box>
                    ))}
                  </Flex>
                </RadioGroup>
              </Flex>
            </FormControl>
          );
        }}
      />
    </Flex>
  );
};
